class ShippingService {
  constructor({
    maxTries = 12,
    pollDelayMs = 500,
  } = {}) {
    this.maxTries = maxTries;
    this.pollDelayMs = pollDelayMs;
  }

  /* --------------------
     ADDRESS
  -------------------- */
  // [Archies 2026-10-19] No third-party IP lookup. The address comes from snippets/js-global-vars.liquid: the market
  // country the shopper is browsing (so each EU market estimates for its own country) + the logged-in customer's
  // address in that country, or the store's per-country line (Theme settings -> Cart -> Estimate address per country).
  // null = no line for this country -> "Calculated at checkout" without a request. ipapi.co was removed: it sent every
  // shopper's IP off-site, capped at ~1,000 lookups/day, and the ZIP it supplied doesn't change our rates.
  estimateAddress() {
    const a = window.shippingEstimateAddress;
    return a && a.country ? { country: a.country, province: a.province || '', zip: a.zip || '' } : null;
  }

  /* --------------------
     SHIPPING RATES
  -------------------- */
  // Perf #3: a single cart:update fans out to several callers (the cart:update listener, cart-progress-
  // bar.handleCartUpdate, and the reco add path), each previously starting an independent up-to-6s
  // shipping poll. Debounce so they collapse into ONE run reflecting the final cart state. All callers
  // within the window share the same pending promise (so none hang); the trailing call wins.
  getShippingRates() {
    if (this._shipDebounce) clearTimeout(this._shipDebounce);
    if (!this._shipPending) {
      this._shipPending = new Promise((resolve, reject) => {
        this._shipResolve = resolve;
        this._shipReject = reject;
      });
    }
    this._shipDebounce = setTimeout(() => {
      const resolve = this._shipResolve;
      const reject = this._shipReject;
      this._shipPending = this._shipResolve = this._shipReject = null;
      this._runShippingRates().then(resolve, reject);
    }, 150);
    return this._shipPending;
  }

  async _runShippingRates() {
    const address = this.estimateAddress();
    if (!address) {
      const placeholder = document.querySelector('.shipping-items-price span');
      if (placeholder) placeholder.textContent = cartShippingStrings().calculatedAtCheckout;
      return;
    }

    await this.prepareShippingRates(address);
    const rates = await this.pollShippingRates(address);

    const el = document.querySelector('.shipping-items-price span');
    const t = cartShippingStrings();

    if (!rates || !rates.length) {
      if (el) el.textContent = t.calculatedAtCheckout;
      return;
    }

    const cheapest = this.pickCheapest(rates);
    const price = Number(cheapest.price);
    // [Archies 2026-10-19] Shopify returns cart shipping rates in the store's base currency (EU: EUR) even when the
    // shopper's market uses another (DK: DKK, SE: SEK). Quoting "€6.69" in a kroner store would mislead, and converting
    // it ourselves can miss checkout's own rounding, so a currency mismatch shows "Calculated at checkout" instead.
    const shopperCurrency = window.Shopify?.currency?.active;
    if (cheapest.currency && shopperCurrency && cheapest.currency !== shopperCurrency) {
      if (el) el.textContent = t.calculatedAtCheckout;
      return;
    }
    // A $0 rate (free-shipping threshold met) reads as FREE; a real rate shows its price; anything
    // unresolvable falls back to "Calculated at checkout". Labels are translatable (locales/*.json).
    let formatted;
    if (price === 0) {
      // [Archies 2026-09-30] Name WHICH free service they earned. Over a sale the cart can qualify
      // for free Standard at one threshold and free Express at a higher one, and the progress bar
      // above has just promised them one of the two by name — "FREE" alone leaves them checking.
      //
      // Read from the REAL rates rather than from the promo thresholds on purpose: this is what
      // checkout will actually offer, so the cart can never promise a service checkout won't give.
      // Among free rates we take the BEST one, not the first, because pickCheapest breaks a $0-vs-$0
      // tie arbitrarily and Express is the better news.
      const free = rates.filter((r) => Number(r.price) === 0);
      const isExpress = (r) => /express|overnight|next[\s-]?day|priority/i.test(rateName(r));
      const best = free.find(isExpress) || free[0] || cheapest;
      formatted = isExpress(best) ? t.freeExpress : (free.length ? t.freeStandard : t.free);
    } else {
      formatted = this.formatMoney(price, this.resolveCurrency(cheapest)) || t.calculatedAtCheckout;
    }

    if (el) el.textContent = formatted;

    // [Archies 2026-09-30] Fold the rate into Estimated Total.
    //
    // Quoting A$7.95 two rows above a "total" that ignores it is the surprise-at-checkout problem
    // in miniature — and worse than saying nothing, because it looks like the number was given.
    // Including it also makes the progress bar concrete: "add another pair for Free Standard
    // Shipping" argues for itself when the charge is sitting in the total.
    this.applyTotal(price, this.resolveCurrency(cheapest));

    return rates;

  }

  async prepareShippingRates(address) {
    // One attempt with the shopper's market country. The old retry with a fixed US/Utah address quoted US rates to
    // shoppers in other countries; a failure now falls back to "Calculated at checkout" (applyShippingRate's catch).
    const params = new URLSearchParams({
      'shipping_address[country]': address.country,
      'shipping_address[province]': address.province,
      'shipping_address[zip]': address.zip,
    });
    const res = await fetch(`${this.root()}cart/prepare_shipping_rates.json?${params.toString()}`, { method: 'POST' });
    if (!res.ok) throw new Error(`prepare_shipping_rates failed (${res.status})`);
  }

  async pollShippingRates(address) {

    const params = new URLSearchParams({
      'shipping_address[country]': address.country,
      'shipping_address[province]': address.province,
      'shipping_address[zip]': address.zip,
    });

    const url = `${this.root()}cart/async_shipping_rates.json?${params.toString()}`;

    for (let attempt = 0; attempt < this.maxTries; attempt++) {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`async_shipping_rates failed (${res.status})`);

      const data = await res.json();
      if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length) {
        return data.shipping_rates;
      }

      await this.sleep(this.pollDelayMs);
    }

    throw new Error('Timed out waiting for shipping rates');
  }

  /* --------------------
     HELPERS
  -------------------- */
  // The current market's path prefix (e.g. /en-dk/), so requests stay in the shopper's market.
  root() {
    return window.Shopify?.routes?.root || '/';
  }

  pickCheapest(rates) {
    return rates.reduce((min, r) =>
      Number(r.price) < Number(min.price) ? r : min
    );
  }

  resolveCurrency(rate) {
    return rate?.currency || window.Shopify?.currency?.active || window.Shopify?.currency?.shop || 'USD';
  }

  /**
   * Rewrite Estimated Total as cart total + shipping.
   *
   * Reads the cart's own figure from the rendered markup in cents, so this never has to model the
   * discounts — whatever Liquid worked out is the starting point, and only shipping is added.
   *
   * Leaves the total untouched when the rate is unknown or unusable: an estimate that quietly
   * omits an unknown is honest, one that contradicts the row above it is not.
   */
  applyTotal(shippingDecimal, currencyCode) {
    const host = document.querySelector('.cart-summary-totals-v2[data-cart-total-cents]');
    const node = document.querySelector('.cart-summary-totals-v2 [data-cart-total]');
    if (!host || !node) return;

    const baseCents = Number(host.dataset.cartTotalCents);
    if (!Number.isFinite(baseCents)) return;
    if (!Number.isFinite(shippingDecimal) || shippingDecimal < 0) return;

    const currency = currencyCode || host.dataset.cartCurrency;
    const total = baseCents / 100 + shippingDecimal;
    const formatted = this.formatMoney(total, currency);
    if (formatted) node.textContent = formatted;
  }

  formatMoney(amountDecimal, currencyCode) {
    if (!Number.isFinite(amountDecimal)) return '';

    // [Archies 2026-09-30] narrowSymbol, so AUD renders "$7.95" and not "A$7.95" (Ethan).
    //
    // Intl qualifies a symbol whenever the formatting locale differs from the currency's home —
    // an en-US browser on the AU store gets "A$". Every other price in this cart comes from
    // Liquid's money filters, which use the store's own format and never qualify, so the shipping
    // line and the total were the only two places a stray "A" appeared. Each store shows its own
    // symbol; the currency is never ambiguous inside one storefront.
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
      }).format(amountDecimal);
    } catch {
      // narrowSymbol is unsupported on a few older engines: fall back rather than lose the price.
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
        }).format(amountDecimal);
      } catch {
        return `${amountDecimal.toFixed(2)} ${currencyCode}`;
      }
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}


// Translatable shipping labels, read from cart-drawer-component[data-i18n] (locales/*.json), with
// English fallbacks so this works even if the bundle is absent.
function cartShippingStrings() {
  const drawer = document.querySelector('cart-drawer-component');
  let t = {};
  if (drawer) {
    try { t = JSON.parse(drawer.getAttribute('data-i18n') || '{}'); } catch (e) { t = {}; }
  }
  return {
    free: t.free || 'FREE',
    freeStandard: t.freeStandard || t.free || 'FREE Standard Shipping',
    freeExpress: t.freeExpress || t.free || 'FREE Express Shipping',
    calculatedAtCheckout: t.calculatedAtCheckout || 'Calculated at checkout',
  };
}

// Shopify returns the service name as `presentment_name` on some endpoints and `name` on others.
// This matches on MERCHANT data (the rate's own name), not on a translated theme string — so a
// renamed or localised shipping service degrades to "FREE Standard Shipping", never to a wrong promise.
function rateName(rate) {
  if (!rate) return '';
  return String(rate.presentment_name || rate.name || rate.title || '');
}

const shippingService = new ShippingService();

// Run on load AND after every cart change — the cart summary is re-rendered (morphSection) on
// cart:update, which resets the shipping line to its server default, so the rate must be re-applied.
// Shipping also genuinely depends on cart contents (weight / free-shipping threshold), so re-fetching
// is correct, not just cosmetic. Any failure falls back to "Calculated at checkout".
function applyShippingRate() {
  shippingService.getShippingRates().catch(function (err) {
    console.warn(err);
    var el = document.querySelector('.shipping-items-price span');
    if (el) el.textContent = cartShippingStrings().calculatedAtCheckout;
  });
}

/**
 * [Archies 2026-09-30] GATED. This used to call applyShippingRate() at module top level, on every
 * page, unconditionally — and this file is loaded from theme.liquid, so it ran on home, PDP and
 * collection too. Measured cost on a page where nobody opened the cart: three uncacheable round
 * trips (ipapi 258ms, prepare_shipping_rates 1592ms, async_shipping_rates 662ms) competing with
 * the LCP image for bandwidth.
 *
 * Checking for `.shipping-items-price` is NOT enough on its own: the cart drawer markup is
 * rendered into every page, so that element exists everywhere. The real signal is whether the
 * summary is actually on screen — visible on /cart, or the drawer having been opened.
 *
 * The rate is still resolved before anyone can read it: the drawer's opening animation is longer
 * than the round trip, and the row renders server-side with the "calculated at checkout"
 * placeholder holding its final height, so nothing shifts when the number lands.
 */
var shippingRateStarted = false;

function startShippingRate() {
  if (shippingRateStarted) return;
  shippingRateStarted = true;
  applyShippingRate();
  document.addEventListener('cart:update', applyShippingRate);
}

function cartSummaryOnScreen() {
  var el = document.querySelector('.shipping-items-price');
  // offsetParent is null for a display:none ancestor, which is the closed drawer.
  return !!(el && el.offsetParent !== null);
}

(function gateShippingRate() {
  // /cart renders the summary in the page itself — resolve immediately, there is no drawer to wait
  // for and the customer is looking straight at it.
  if (cartSummaryOnScreen()) { startShippingRate(); return; }

  var drawer = document.querySelector('cart-drawer-component dialog, dialog.cart-drawer__dialog');

  if (drawer && typeof MutationObserver === 'function') {
    if (drawer.open) { startShippingRate(); return; }
    var mo = new MutationObserver(function () {
      if (!drawer.open) return;
      mo.disconnect();
      startShippingRate();
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    return;
  }

  // No dialog to watch — the optimistic cart can insert the drawer later, and a customer who never
  // gets a rate is worse than a customer who gets one early. Fall back to the opener click.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-cart-drawer-open], .header-actions__cart-icon, a[href*="/cart"]')) {
      startShippingRate();
    }
  }, true);
  document.addEventListener('cart:update', startShippingRate);
})();

